import { captureConsoleIntegration } from '@sentry/integrations'
import * as Sentry from '@sentry/react'
import React from 'react'
import ReactDOM from 'react-dom/client'
import { Provider } from 'react-redux'
import { RouterProvider } from 'react-router-dom'
import { AuthContextProvider } from './context/AuthContextProvider'
import './css/index.css'
import { store } from './store/store.ts'
import { Userpilot } from 'userpilot'
import { AppInitPostAuth } from './app-init/AppInitPostAuth.tsx'
import { AppInit } from './app-init/AppInit.tsx'
import { upNavigationHelper } from './chat-common/conversation-navigation/util/up-navigation-helper.ts'
import { kProduction, kStaging } from './constants/constants-ui.ts'
import { HubspotConversationsProvider } from './context/HubspotConversationsProvider.tsx'
import { isNoReportFirebaseErrorEvent } from './util/sentry/is-no-report-firebase-error-event.ts'
import { redactSensitiveSentryEventData } from './util/sentry/redact-sensitive-event-data.ts'
import { router } from './routing/router'
import { FeatureFlagsProvider } from './feature-flags/FeatureFlagsProvider'
import { PWAUpdateProvider } from './context/PWAUpdateProvider'
import { isOutlookPrivacyMode } from './analytics/privacy/outlook-continuation-privacy'

// Global window property declarations (type defs)
declare global {
  interface Window {
    // Hubspot stuff - Disable eslint for any type as it is a global window property
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    hsConversationsSettings: Record<string, any>
    hsConversationsOnReady: Array<() => void>
    HubSpotConversations: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      on: any
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      off: any
      widget: {
        status: () => { loaded: boolean; pending: boolean }
        load: (params?: { widgetOpen: boolean }) => void
        remove: () => void
        open: () => void
        close: () => void
        refresh: (openToNewThread?: boolean) => void
      }
    }
    // Userpilot stuff
    goToNewAssistantChat: () => void
    openSupportChat?: () => void
    userpilot?: {
      reload: () => void
    }
  }
}

const shouldInitSentry = (kProduction || kStaging) && !import.meta.env.VITE_SENTRY_DISABLED && !isOutlookPrivacyMode()

// Add sentry error logging for production and staging environments
if (shouldInitSentry) {
  // console.log(`Initializing sentry with environment: ${import.meta.env.MODE} and version ${APP_VERSION}`)
  Sentry.init({
    dsn: 'https://82f4e2d22d504875b81bd29ebd8a2e30@o4505370434928640.ingest.sentry.io/4505432129994752',
    environment: import.meta.env.MODE,
    release: APP_VERSION,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: false,
        // Replay records its own network entries, which never pass through beforeSend and would
        // otherwise carry signed storage URLs into the replay.
        beforeAddRecordingEvent: (event) => redactSensitiveSentryEventData(event),
      }),
      captureConsoleIntegration({
        levels: ['error'],
      }),
    ],

    // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
    // staging, prod, local
    tracePropagationTargets: ['https://paxton-app-api-mla66wcquq-uc.a.run.app', 'https://api2.paxton.ai', 'http://localhost'],

    // Performance Monitoring
    tracesSampleRate: 0.1, // Capture 10% of the transactions

    // Session Replay
    replaysSessionSampleRate: 0.5, // This sets the sample rate at 50%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    // Ignore errors containing these strings / expressions
    // These are strings inside the error message content (reported even without the stack trace)
    ignoreErrors: [
      'Amplitude Logger [Error]',

      // Match any error message starting with the specified phrase:
      /^Error sending Amplitude event/i,

      // Add patterns for analytics platform error strings
      /POST.*api2\.amplitude\.com/i,

      // LottieLoader reports these failures once per asset with browser and PWA context.
      /^Failed to load animation data from URL: \/animations\/(?:branded-loader|infinite-loader(?:-color)?)\.lottie\./,
    ],

    // Error deny list - don't report errors from these urls
    // These are the urls that are reported in the error stack trace
    denyUrls: [
      // Amplitude errors
      /api2\.amplitude\.com/i,

      // userpilot
      /js\.userpilot\.io/i,

      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
    ],

    // Allows evaluation of events before sending them to Sentry
    //
    // Modification: Enables us to modify the event before sending it to Sentry
    //  - Example: strip the user's email address from the event for enterprise clients
    //
    // Filtering: Enables us to ignore events that cannot be filtered by
    //  - ignoreErrors
    //  - denyUrls
    beforeSend(event) {
      // Drop expected Firebase auth error events
      const isNoReportFirebaseError = isNoReportFirebaseErrorEvent(event)
      if (isNoReportFirebaseError) {
        return null
      }

      return redactSensitiveSentryEventData(event)
    },

    beforeSendTransaction(event) {
      return redactSensitiveSentryEventData(event)
    },
  })

  // Initialize Userpilot
  if (kProduction) {
    // Initialize Userpilot for production
    Userpilot.initialize('NX-efe1461d')
  } else if (kStaging) {
    if (import.meta.env.VITE_USERPILOT_DISABLED) {
      console.log('Preview channel detected - not initializing Userpilot')
    } else {
      // Initialize Userpilot for staging
      Userpilot.initialize('STG-NX-efe1461d')
    }
  }
}

// Initialize the navigation helper for Userpilot tooltips
upNavigationHelper.initialize((path: string) => {
  router.navigate(path)
})

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <Provider store={store}>
      <AppInit>
        <AuthContextProvider>
          <FeatureFlagsProvider>
            <AppInitPostAuth>
              <HubspotConversationsProvider>
                <PWAUpdateProvider>
                  <RouterProvider router={router} />
                </PWAUpdateProvider>
              </HubspotConversationsProvider>
            </AppInitPostAuth>
          </FeatureFlagsProvider>
        </AuthContextProvider>
      </AppInit>
    </Provider>
  </React.StrictMode>
)
